import { useRequestUserRequiredFieldsQuery } from '@/core/services/SettingsManagement.service';
import {
  UserRequiredFieldsProfile,
  FormConfig,
  UserRequiredFieldsSettingsValue,
  UserData,
} from '@/features/auth/types/User.types';
import { object, string, StringSchema, ObjectSchema } from 'yup';
import { CommonValidationTranslations } from '@/core/utils/commonValidationTranslations';
import { t } from '@lingui/core/macro';
import { PROFILE_FIELDS, resolveProfile } from '@/features/auth/utils/userRequiredFields';
import { useRequestMeDataQuery } from '../../account/services/Account.service';

const generateStringSchema = (config: FormConfig): StringSchema => {
  const { required } = config;

  let schema = string().trim();

  if (required) {
    schema = schema.required(() => CommonValidationTranslations.requiredField());
  }

  return schema;
};

export const useProfileSchema = (): {
  profile: UserRequiredFieldsProfile;
  validationSchema: ObjectSchema<any>;
  isFetching: boolean;
  isValid: (meData: UserData) => boolean;
} => {
  const { data, isFetching } = useRequestUserRequiredFieldsQuery();
  const { data: userData } = useRequestMeDataQuery();

  const shape = {} as Record<keyof UserRequiredFieldsSettingsValue['profile'], StringSchema>;

  // resolveProfile fills in any field the backend omitted (e.g. a tenant setting
  // that predates newer fields) so `profile[field]` is never undefined downstream.
  const profile = resolveProfile(data?.value?.profile);

  PROFILE_FIELDS.forEach((field) => {
    const { visible } = profile[field];
    if (visible) {
      shape[field] = generateStringSchema(profile[field]);
    }
  });

  if (shape.email) {
    shape.email = shape.email.email(() =>
      CommonValidationTranslations.invalidField(
        t({
          id: 'auth.forms.email',
          message: 'E-mail',
        }),
      ),
    );
  }

  if (shape.street && !profile.houseNumber.visible) {
    if (!userData?.street || profile.street.editable) {
      const addressPattern = /^(?=.*\d)(?=.*[a-zA-Z])(?!^\d+$).+$/;

      shape.street = shape.street.matches(addressPattern, {
        message: CommonValidationTranslations.invalidAddress(),
      });
    }
  }

  const validationSchema = object().shape(shape);

  const isValid = (meData: UserData) => {
    try {
      validationSchema.validateSync(meData); // Throws if invalid
      return true;
    } catch (err) {
      return false;
    }
  };

  return { profile, validationSchema, isFetching, isValid };
};
